import type { LayerData } from '@apps/card/routes/CardCustomizer/components/Layer.types';
import { absolutePhotoUrl } from './mediaService';

/**
 * Tells a monochrome "ink glyph" apart from artwork, by measuring the image itself.
 *
 * Some template marks are ink rather than pictures — the monogram separator, holiday ornaments —
 * and should follow the card's text colour when a customer matches the back to the front. They
 * carry their colour in their pixels, so recolouring means masking: keep the alpha as a stencil and
 * paint a flat colour through it (see `ImageLayer`).
 *
 * That is destructive on anything else — mask a photograph and you get a solid silhouette of it —
 * so which layers qualify has to be decided correctly, and nothing in the template JSON says. All
 * 147 non-editable image layers across the back catalogue are unnamed, and geometry lies: the
 * portrait monogram's slash is a 1500x2100 image that is 99.99% transparent, drawn full-bleed, so
 * any size rule reads it as a full-page illustration.
 *
 * Only the pixels distinguish them, measured against real assets:
 *
 * | asset                          | opaque | colours | verdict |
 * |--------------------------------|--------|---------|---------|
 * | monogram slash (full-bleed)    |  0.00  |    1    | ink     |
 * | monogram slash (18x18)         |  0.07  |    1    | ink     |
 * | photo_monogram photo           |  1.00  |  215    | artwork |
 * | plaid_red background           |  1.00  |    4    | artwork |
 * | large_photo ornament           |  0.06  |    2    | ink     |
 *
 * Both conditions are load-bearing: `plaid_red`'s background has only four colours, so colour count
 * alone nearly misfires, and opacity is what separates it.
 */

/** Above this share of opaque pixels an image is covering its box, so it is a picture, not a mark. */
const MAX_OPAQUE_SHARE = 0.5;

/** Distinct colours allowed among opaque pixels, after quantising away anti-aliasing noise. */
const MAX_DISTINCT_COLORS = 2;

/**
 * How far per channel a pixel may sit from the mark's own colour and still count as that colour.
 *
 * Wide enough to absorb what the pipeline does to a flat ink before the probe ever sees it — a
 * lossy WebP transcode, then a downscale of roughly 17x into the sample — and far narrower than
 * the gap between a colour and a different one.
 */
const INK_COLOR_TOLERANCE = 32;

/** Share of opaque pixels that must lie within `INK_COLOR_TOLERANCE` of the mark's own colour. */
const MIN_INK_COLOR_SHARE = 0.9;

/** Coarse colour key, 3 bits per channel, used only to find roughly where the mark's colour sits. */
const coarseKey = (r: number, g: number, b: number) => (r >> 5) * 64 + (g >> 5) * 8 + (b >> 5);

/** Alpha below this reads as background rather than ink, and is what anti-aliased edges fade to. */
const MIN_INK_ALPHA = 32;

/** Long edge the probe samples at. Big enough to catch a thin mark, small enough to stay cheap. */
const SAMPLE_MAX_EDGE = 128;

/**
 * Whether RGBA pixels describe a monochrome mark on a transparent field.
 *
 * Pure, so the thresholds above are testable without a canvas. Colours are quantised to 4 bits per
 * channel: a glyph's anti-aliased edges blend toward the background and would otherwise register as
 * dozens of distinct colours.
 */
export function isInkGlyphPixels(data: Uint8ClampedArray | ReadonlyArray<number>, pixelCount: number): boolean {
  if (pixelCount <= 0) return false;
  let opaque = 0;
  const colors = new Set<number>();
  for (let i = 0; i < pixelCount * 4; i += 4) {
    if (data[i + 3] <= MIN_INK_ALPHA) continue;
    opaque++;
    colors.add(((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4));
    if (colors.size > MAX_DISTINCT_COLORS) return false;
  }
  if (opaque === 0) return false;
  return opaque / pixelCount <= MAX_OPAQUE_SHARE;
}

/**
 * `isInkGlyphPixels` with one extra way to qualify: nearly every pixel being the *same* colour,
 * measured by distance rather than by bucket equality.
 *
 * Quantising to 4 bits is what lets `isInkGlyphPixels` ignore anti-aliasing, but it puts hard walls
 * between buckets and a colour sitting on one scatters across them. `nostalgia_tan`'s "kindly
 * deliver to" is a single tan, and by the time the probe sees it — served as a lossy WebP, then
 * squashed from 2175x1575 into a 128x128 sample — it lands in four buckets. Rejected for a
 * counting artefact rather than anything about the image.
 *
 * So instead of asking how many buckets are occupied, this finds roughly where the mark's colour
 * sits, takes the centroid of the pixels there, and asks what share of the mark lies within
 * `INK_COLOR_TOLERANCE` of it. Blending noise moves a colour a little; a second colour is a long
 * way off. Measured in-browser on the real assets the separation is total: all six single-ink
 * envelope marks score 1.000, while the two that mix lettering into artwork score 0.116
 * (`bohemian_eucalyptus`) and 0.402 (`love_and_leaves`). It is also stable where bucket counting is
 * not — `nostalgia_tan` scores 1.000 whether sampled at 128 or 256, where its dominant-bucket share
 * swings 0.878 to 0.816.
 *
 * A superset of `isInkGlyphPixels` by construction: the bucket test is kept as a fast path, so no
 * image the back rule masks can be rejected here, and the opacity condition is untouched and still
 * does the work of rejecting patterns and photographs.
 *
 * **Envelope-only, and it must stay that way.** The back's assets are not this catalogue: probing
 * all 139 of its non-editable image layers, a looser rule starts masking `large_photo_gold`, which
 * is 45% opaque and 97.7% one colour, and would be painted in the front's ink. Right answer on
 * envelopes, wrong answer on backs, so the back keeps `isInkGlyphPixels` untouched.
 */
export function isEnvelopeInkGlyphPixels(data: Uint8ClampedArray | ReadonlyArray<number>, pixelCount: number): boolean {
  if (pixelCount <= 0) return false;
  let opaque = 0;
  const fine = new Set<number>();
  const coarse = new Map<number, number>();
  for (let i = 0; i < pixelCount * 4; i += 4) {
    if (data[i + 3] <= MIN_INK_ALPHA) continue;
    opaque++;
    fine.add(((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4));
    const key = coarseKey(data[i], data[i + 1], data[i + 2]);
    coarse.set(key, (coarse.get(key) ?? 0) + 1);
  }
  if (opaque === 0 || opaque / pixelCount > MAX_OPAQUE_SHARE) return false;
  if (fine.size <= MAX_DISTINCT_COLORS) return true;

  let modal = 0;
  let modalCount = -1;
  for (const [key, count] of coarse) {
    if (count > modalCount) {
      modalCount = count;
      modal = key;
    }
  }
  // The modal bucket says roughly where the ink is; its centroid says precisely, without the
  // bucket's walls rounding pixels that sit near one.
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  for (let i = 0; i < pixelCount * 4; i += 4) {
    if (data[i + 3] <= MIN_INK_ALPHA || coarseKey(data[i], data[i + 1], data[i + 2]) !== modal) continue;
    sumR += data[i];
    sumG += data[i + 1];
    sumB += data[i + 2];
  }
  const centerR = sumR / modalCount;
  const centerG = sumG / modalCount;
  const centerB = sumB / modalCount;

  let near = 0;
  for (let i = 0; i < pixelCount * 4; i += 4) {
    if (data[i + 3] <= MIN_INK_ALPHA) continue;
    const distance = Math.max(Math.abs(data[i] - centerR), Math.abs(data[i + 1] - centerG), Math.abs(data[i + 2] - centerB));
    if (distance <= INK_COLOR_TOLERANCE) near++;
  }
  return near / opaque >= MIN_INK_COLOR_SHARE;
}

/** How a probe reads its sampled pixels. The two rules differ only in this. */
type InkGlyphClassifier = (data: Uint8ClampedArray | ReadonlyArray<number>, pixelCount: number) => boolean;

/**
 * Media-service filenames carry a content hash, so a src always maps to the same bytes and a
 * verdict never goes stale. Promises are cached so concurrent layers sharing an asset probe once.
 *
 * Keyed by rule as well as src: the two classifiers disagree about `nostalgia_tan` by design, and
 * a src-only key would let whichever surface probed first answer for the other.
 */
const verdicts = new Map<string, Promise<boolean>>();

async function probe(src: string, classify: InkGlyphClassifier): Promise<boolean> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  const url = absolutePhotoUrl(src);
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`could not load ${url}`));
      image.src = url;
    });
    const width = Math.min(image.naturalWidth, SAMPLE_MAX_EDGE);
    const height = Math.min(image.naturalHeight, SAMPLE_MAX_EDGE);
    if (!width || !height) return false;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return false;
    context.drawImage(image, 0, 0, width, height);
    // Throws if the canvas is tainted, i.e. the host refused CORS.
    const { data } = context.getImageData(0, 0, width, height);
    return classify(data, width * height);
  } catch {
    // Fail closed. A glyph left in its own colour is a cosmetic miss; masking a photograph by
    // mistake replaces it with a silhouette, so an unmeasurable image is never treated as ink.
    return false;
  }
}

/**
 * The image srcs among `layers` that `classify` calls ink glyphs.
 *
 * Customer photos (`editable`) are excluded before any network work — they are never ink, and
 * probing them would be pure cost.
 */
async function findGlyphSrcs(layers: ReadonlyArray<LayerData>, rule: string, classify: InkGlyphClassifier): Promise<ReadonlySet<string>> {
  const candidates = [...new Set(layers.filter(layer => layer.type === 'image' && !layer.editable).map(layer => (layer as { imageData: { src: string } }).imageData.src))];
  const results = await Promise.all(
    candidates.map(src => {
      const key = `${rule}:${src}`;
      const cached = verdicts.get(key) ?? probe(src, classify);
      verdicts.set(key, cached);
      return cached.then(isGlyph => [src, isGlyph] as const);
    }),
  );
  return new Set(results.filter(([, isGlyph]) => isGlyph).map(([src]) => src));
}

/** Ink glyphs on a card back, for `applyFrontColorsToBack` to fill. */
export function findInkGlyphSrcs(layers: ReadonlyArray<LayerData>): Promise<ReadonlySet<string>> {
  return findGlyphSrcs(layers, 'back', isInkGlyphPixels);
}

/**
 * Ink glyphs on an envelope, for `applyFrontColorsToEnvelopeLayers` to fill.
 *
 * Separate from `findInkGlyphSrcs` because of the classifier, not the layers — see
 * `isEnvelopeInkGlyphPixels` for why the looser rule must not reach the card back.
 */
export function findEnvelopeInkGlyphSrcs(layers: ReadonlyArray<LayerData>): Promise<ReadonlySet<string>> {
  return findGlyphSrcs(layers, 'envelope', isEnvelopeInkGlyphPixels);
}
